import { z } from 'zod';

import { GatheringStatusSchema, GatheringVenueStatusSchema } from '../enums';
import { OffPeakWindowSchema } from './admin';
import { IdSchema, KopecksSchema } from './common';
import { ActivityRefSchema } from './gathering';

/**
 * Портал площадки (BLUEPRINT Stage 14, задача 2): роль `VENUE`, отдельный layout в `apps/admin`.
 * Площадка видит только брони на своих площадках: сбор, время, число людей — без состава и
 * без персональных данных участников (3.3: публичных карточек людей не бывает).
 */

export const VenuePortalVenueSchema = z.object({
  id: IdSchema,
  name: z.string(),
  address: z.string(),
  cityName: z.string(),
  timezone: z.string(),
  pricePerHourKopecks: KopecksSchema.nullable(),
  /** Сколько человек площадка принимает за слот — по нему портал предупреждает о наложении */
  capacity: z.number().int().positive().nullable(),
  /** Контакт площадки: портал умел его записывать, но не показывал обратно */
  contact: z.string().nullable(),
  /**
   * Что площадка принимает. Пустой список — сбор сюда не поставить: конструктор сезона
   * предлагает площадку только по отмеченной активности (раунд 3, O7)
   */
  activities: z.array(ActivityRefSchema),
  offPeak: z.array(OffPeakWindowSchema),
});
export type VenuePortalVenue = z.infer<typeof VenuePortalVenueSchema>;

/** Что видит площадка о себе при входе. */
export const VenuePortalMeSchema = z.object({
  venues: z.array(VenuePortalVenueSchema),
  /** Активности городов площадки — из них площадка отмечает, что принимает */
  activities: z.array(ActivityRefSchema),
  /** Броней ждут решения */
  pending: z.number().int().nonnegative(),
});
export type VenuePortalMe = z.infer<typeof VenuePortalMeSchema>;

export const VenueBookingRowSchema = z.object({
  gatheringId: IdSchema,
  venueId: IdSchema,
  venueName: z.string(),
  /** Начало сбора, ISO UTC; таймзона города — в `timezone` */
  startsAt: z.string(),
  timezone: z.string(),
  durationMin: z.number().int().positive(),
  /** Название активности: «Падел», «Мини-футбол» */
  activityName: z.string(),
  /**
   * Раунд 3, V3: ответили «иду». Раньше при нуле ответов сюда подставлялся размер состава,
   * и площадка читала «8 / 8» за неделю до сбора, когда не ответил никто.
   */
  going: z.number().int().nonnegative(),
  /** Сколько участников вообще ответили на сбор; 0 — «ответов пока нет» */
  answered: z.number().int().nonnegative(),
  /** Сейчас в составе отряда — из них и отвечают */
  inSquad: z.number().int().nonnegative(),
  /** Размер состава отряда — верхняя граница; по ней считается наложение на вместимость */
  capacity: z.number().int().positive(),
  /** Обезличенное имя отряда: название или «Отряд № 3» — без списка участников */
  squadLabel: z.string(),
  status: GatheringStatusSchema,
  venueStatus: GatheringVenueStatusSchema,
  venueStatusAt: z.string().nullable(),
  venueNote: z.string().nullable(),
  /** Стоимость слота по прайсу площадки, копейки */
  slotPriceKopecks: KopecksSchema.nullable(),
});
export type VenueBookingRow = z.infer<typeof VenueBookingRowSchema>;

export const VenueBookingsInputSchema = z
  .object({
    venueId: IdSchema.optional(),
    /** `upcoming` — впереди, `past` — прошедшие, `pending` — ждут решения */
    scope: z.enum(['upcoming', 'past', 'pending']).default('upcoming'),
    limit: z.number().int().min(1).max(200).default(50),
  })
  .default({ scope: 'upcoming', limit: 50 });
export type VenueBookingsInput = z.infer<typeof VenueBookingsInputSchema>;

/** Быстрые причины отказа в форме портала; текст — в `texts.venuePortal.declineReasons` админки. */
export const VENUE_DECLINE_REASONS = ['busy', 'closed', 'repair', 'capacity'] as const;
export type VenueDeclineReason = (typeof VENUE_DECLINE_REASONS)[number];

/**
 * Решение площадки по брони. `CONFIRMED` по отклонённой брони — «Вернуть бронь»: оператор получает
 * уведомление (раунд 4, V2). Отказ — только с причиной (раунд 4, V3): без неё оператору нечем
 * решать, искать ли другое время или другую площадку.
 */
export const VenueDecisionInputSchema = z
  .object({
    gatheringId: IdSchema,
    decision: z.enum(['CONFIRMED', 'DECLINED']),
    note: z.string().trim().max(500).nullable().default(null),
  })
  .superRefine((v, ctx) => {
    if (v.decision === 'DECLINED' && !v.note) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['note'],
        message: 'Укажи причину отказа',
      });
    }
  });
export type VenueDecisionInput = z.infer<typeof VenueDecisionInputSchema>;

/** Непиковые окна и цена часа — единственное, что площадка правит сама. */
export const VenueSelfUpdateInputSchema = z.object({
  venueId: IdSchema,
  pricePerHourKopecks: KopecksSchema.nullable().optional(),
  offPeak: z.array(OffPeakWindowSchema).max(14).optional(),
  contact: z.string().trim().max(200).nullable().optional(),
  /** Вместимость за слот: без неё площадка подтверждает больше броней, чем у неё мест */
  capacity: z.number().int().positive().max(999).nullable().optional(),
  /** Что площадка принимает: иначе падел-клубу приходят заявки на баню и мастерскую */
  activityIds: z.array(IdSchema).max(30).optional(),
});
export type VenueSelfUpdateInput = z.infer<typeof VenueSelfUpdateInputSchema>;

export const VenueStatsWeekSchema = z.object({
  /** Понедельник недели, `YYYY-MM-DD` */
  week: z.string(),
  bookings: z.number().int().nonnegative(),
  /** Минут подтверждённых слотов (без дублей одного времени); раньше — дробные часы «7.5» */
  minutes: z.number().int().nonnegative(),
  /** Ответили «иду» — без подстановки размера состава */
  people: z.number().int().nonnegative(),
  /** Выручка по прайсу: подтверждённые слоты, одно время площадки — один раз */
  revenueKopecks: KopecksSchema,
});
export type VenueStatsWeek = z.infer<typeof VenueStatsWeekSchema>;

export const VenueStatsSchema = z.object({
  venueId: IdSchema,
  venueName: z.string(),
  /**
   * Броней за период. Правила отбора те же, что у списка «Прошедшие» (раунд 3, V5): сборы
   * опубликованных сезонов, начавшиеся за `weeks` недель; отменённые не считаются.
   */
  bookings: z.number().int().nonnegative(),
  confirmed: z.number().int().nonnegative(),
  declined: z.number().int().nonnegative(),
  /** Броней без решения площадки */
  noAnswer: z.number().int().nonnegative(),
  /**
   * Средняя заполняемость: «иду» к вместимости площадки (или размеру состава) по броням, где
   * кто-то ответил; без ответов — `null`, а не выдуманные 100 %
   */
  fillPct: z.number().int().min(0).max(100).nullable(),
  /** Выручка по прайсу: только подтверждённые слоты, одно время площадки — один раз, копейки */
  revenueKopecks: KopecksSchema,
  weeks: z.array(VenueStatsWeekSchema),
});
export type VenueStats = z.infer<typeof VenueStatsSchema>;

export const VenueStatsInputSchema = z
  .object({ venueId: IdSchema.optional(), weeks: z.number().int().min(2).max(26).default(12) })
  .default({ weeks: 12 });
export type VenueStatsInput = z.infer<typeof VenueStatsInputSchema>;

// ───────────────────────────── управление доступом из админки ─────────────────────────────

export const AdminVenueUserAddInputSchema = z.object({
  venueId: IdSchema,
  /** Telegram ID сотрудника площадки — он должен один раз открыть бота */
  tgId: z.string().regex(/^\d{1,19}$/, 'Telegram ID — число'),
});
export type AdminVenueUserAddInput = z.infer<typeof AdminVenueUserAddInputSchema>;

export const AdminVenueUserRemoveInputSchema = z.object({
  venueId: IdSchema,
  userId: IdSchema,
});
export type AdminVenueUserRemoveInput = z.infer<typeof AdminVenueUserRemoveInputSchema>;

export const AdminVenueUserRowSchema = z.object({
  userId: IdSchema,
  tgId: z.string(),
  name: z.string(),
  username: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
});
export type AdminVenueUserRow = z.infer<typeof AdminVenueUserRowSchema>;
